import { User } from "@/models/users/User";
import { FC } from "react";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@capacit-com/lara-law-shared-ui";
import { useThemeToggle } from "@/hooks/useThemeToggle";
import { useAuth0 } from "@auth0/auth0-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  Bell,
  ChevronsUpDown,
  LogOut,
  SunMoon,
  Building2,
  Megaphone,
} from "lucide-react";
import { handleLogout, showOrganizationPicker } from "@/utils/auth";
import { useKeyboardShortcut, useMetaKey } from "@/utils/shortcut";
import { useResetStores } from "@/hooks/useResetStores";
import UserAvatar from "../images/UserAvatar";
import { useNotificationSettingsDialogStore } from "@/stores/notification-settings-dialog.store";
import { useUnreadReleaseNote } from "@/hooks/useReleaseNotes";
import useReleaseNotesStore from "@/stores/release-notes.store";

type NavUserProps = {
  user: User;
};

const NavUser: FC<NavUserProps> = ({ user }) => {
  /**** Hooks ****/
  const { isMobile, setOpenMobile } = useSidebar();
  const { loginWithRedirect, logout } = useAuth0();
  const { isDarkMode, toggleTheme } = useThemeToggle();
  const { isMac } = useMetaKey();
  const { resetStores } = useResetStores();
  const openNotificationSettings = useNotificationSettingsDialogStore(
    (state) => state.setOpen
  );
  const openReleaseNotes = useReleaseNotesStore((state) => state.setDialogOpen);

  /**
   * Opens a dialog from the menu. On a phone the menu lives in the sidebar
   * sheet; closing the sheet first keeps it from sitting under the dialog and
   * from remounting on top of it after a resize across the md breakpoint.
   */
  const openDialog = (setOpen: (open: boolean) => void) => {
    setOpenMobile(false);
    setOpen(true);
  };
  const { hasUnread: hasUnreadReleaseNote } = useUnreadReleaseNote();

  useKeyboardShortcut({
    key: "d",
    callback: toggleTheme,
  });

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer group-data-[collapsible=icon]:justify-center"
            >
              <span className="relative">
                <UserAvatar
                  user={user}
                  avatarClassName="h-8 w-8 rounded-lg"
                  fallbackClassName="rounded-lg"
                />
                {hasUnreadReleaseNote && (
                  <span
                    className="ring-sidebar absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-primary ring-2"
                    aria-hidden="true"
                  />
                )}
              </span>
              {hasUnreadReleaseNote && (
                <span className="sr-only">Nye ændringer i ændringsloggen</span>
              )}
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">
                  {user.firstName} {user.lastName}
                </span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <UserAvatar user={user} avatarClassName="h-8 w-8 rounded-lg" />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {user.firstName} {user.lastName}
                  </span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {user.tenantIds.length > 1 && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    resetStores();
                    showOrganizationPicker(loginWithRedirect);
                  }}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Building2 className="size-4" />
                  Skift organisation
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => openDialog(openNotificationSettings)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Bell className="size-4" />
                Notifikationer
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openDialog(openReleaseNotes)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Megaphone className="size-4" />
                Ændringslog
                {hasUnreadReleaseNote && (
                  <span
                    className="ml-auto size-2 rounded-full bg-primary"
                    aria-hidden="true"
                  />
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  toggleTheme();
                }}
                className="flex justify-between cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <SunMoon />
                  {isDarkMode ? "Light mode" : "Dark mode"}
                </div>

                <div className="text-xs text-muted-foreground">
                  {isMac ? "⌘" : "Ctrl"} + D
                </div>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                handleLogout(logout);
              }}
              className="cursor-pointer"
            >
              <LogOut />
              Log ud
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

export default NavUser;
